<template>
  <div
    data-cy="cart-controls"
    class="order-last flex-col gap-y-5 gap-x-20 grid grid-cols-1 md:gap-x-5"
    :class="{
      'md:grid-cols-2': showAddToCartButton,
      'md:grid-cols-1': !showAddToCartButton,
    }"
  >
    <div
      v-if="!disableControlState"
      class="qty-control-input relative [ flex items-center ] space-x-2 h-12 md:w-1/2 border border-primary-2-06 rounded-2xl"
    >
      <template v-if="qtyControlState === 'PASSIVE'">
        <QtyControls
          v-model="quantity"
          :min="Number(product.cartControl.min)"
          :max="Number(product.cartControl.max)"
          :step="Number(product.cartControl.step)"
          :show-step-count="false"
          :options="options"
          class="mt-auto h-full"
          :variant="'secondary'"
          strict
        />
      </template>

      <template v-if="qtyControlState === 'ACTIVE'">
        <ProductCardQtyControl
          :key="product.sku || ''"
          :sku="product.sku || ''"
          :parent-sku="product.parentSku"
          :min="Number(product.cartControl.min)"
          :max="Number(product.cartControl.max)"
          :step="Number(product.cartControl.step)"
          :stock="product.stock || 0"
          :options="options"
          :show-step-count="false"
          class="mt-auto w-full h-full"
          type="product-card"
        >
          <template #empty-place-holder>
            <button
              data-attr="productCard_AddToCart"
              class="mx-auto w-full [ flex justify-center items-center ] h-12 font-medium rounded-2xl [ bg-primary-1-02 border-primary-1-02 text-white ] [ text-center text-base uppercase tracking-widest whitespace-nowrap ]"
              @click="handleSendAddToCartEvent"
            >
              {{ $t('addToCart') }}
            </button>
          </template>
        </ProductCardQtyControl>
      </template>
    </div>

    <AppButton
      v-if="showAddToCartButton"
      data-cy="add-to-cart"
      data-attr="innerPage__addToCart"
      :disabled="!product.stock"
      class="[ w-full ] [ md:flex justify-center ]"
      disable-hover
      :inverted="disableControlState"
      :class="{ 'opacity-50 pointer-events-none': !product.stock, 'border border-primary-1-02': product.stock }"
      @click="addToCart"
    >
      {{ product.stock ? $t('addToCart') : $t('outOfStock') }}
    </AppButton>
  </div>
</template>
<script setup lang="ts">
// import { isWeightedItem } from '~/utils/product';

import type {
  CustomizableOptionInput,
  EnteredOptionInput,
  ProductContext,
  ProductData,
} from '@robustastudio/e-commerce/common';

/**
 *
 * helper functions
 */

/**
 * shouldn't display add to cart button
 */
function shouldShowAddToCartButton(product: ProductContext): boolean {
  if (product.type === 'SimpleProduct' && !product.parentSku) {
    return false;
  }

  if (product.type === 'ConfigurableProduct' && product.parentSku) {
    return false;
  }

  if (product.type === 'SimpleProduct' && product.parentSku) {
    return false;
  }

  return true;
}

/**
 * ACTIVE: update quantity control upon changing the quantity
 * PASSIVE: have to click add to cart to apply
 *
 */
function qtyControlType(product: ProductContext): 'ACTIVE' | 'PASSIVE' {
  if (product.type === 'SimpleProduct') {
    return 'ACTIVE';
  }

  if (product.type === 'ConfigurableProduct' && product.parentSku) {
    return 'ACTIVE';
  }

  return 'PASSIVE';
}

function shouldDisableQtyButton(product: ProductData): boolean {
  if (product.type === 'GroupedProduct') {
    return true;
  }
  return false;
}

/**
 *
 * Based on the provided design
 *
 * case 1: for weighted items products hide add to cart and display add to cart aggressive item
 * case 2: for grouped items disable quantity controllers and show add to cart only
 * case 3: for bundle items show passive quantity controllers and add to cart only
 */
const { product } = useProductContext();
const { giftWrappingComment } = useProductGiftWrappingContext();
const { quantity, addToCart: add } = useAddProductToCartContext();
const { error } = useAlerts();
const { emit } = useEventBus();
const { t } = useI18n();
const { addToCart: addToCartEvent } = useGtmEvents();

function handleSendAddToCartEvent() {
  addToCartEvent({
    currency: 'EGP',
    name: product.value.name || '',
    mainCategory: product.value.categories?.[0]?.name || '',
    sku: product.value.sku || '',
    unitPrice: product.value.price,
    quantity: quantity.value,
    id: [product.value.sku || ''],
  });
}

async function addToCart() {
  try {
    await add();
    emit('ITEM_ADDED', product.value);
    handleSendAddToCartEvent();
  } catch (err) {
    if (/the requested qty is not available/i.test((err as Error).message)) {
      error(t('qtyUnavailable', [product.value?.name]) as string);
      return;
    }

    if (
      /Product that you are trying to add is not available/i.test((err as Error).message) ||
      /Some of the products are out of stock/i.test((err as Error).message)
    ) {
      error(t('outOfStockError', [product.value?.name]) as string);
    }
  }
}

const options = computed<{
  entered_options?: Array<EnteredOptionInput>;
  customizable_options?: Array<CustomizableOptionInput>;
}>(() => {
  return {
    ...(product.value.simpleGiftBoxOptionId
      ? {
          entered_options: [
            {
              uid: product.value.simpleGiftBoxOptionId,
              value: giftWrappingComment.value || 'N/A',
            },
          ],
        }
      : {}),
    ...(product.value.configurableGiftBoxOptionId
      ? {
          customizable_options: [
            {
              id: product.value.configurableGiftBoxOptionId,
              value_string: giftWrappingComment.value || 'N/A',
            },
          ],
        }
      : {}),
  };
  // return product.value.giftBoxOptionId
  //   ? [
  //       {
  //         id: product.value.giftBoxOptionId,
  //         value: giftWrappingComment.value || 'N/A',
  //       },
  //     ]
  //   : [];
});
const qtyControlState = computed(() => qtyControlType(product.value));
const disableControlState = computed(() => shouldDisableQtyButton(product.value));

const showAddToCartButton = computed(() => {
  return shouldShowAddToCartButton(product.value);
});
</script>

<i18n>
{
  "en": {
    "addToCart": "Add To Cart",
    "outOfStock": "Out of Stock"
  },
  "ar": {
    "addToCart": "أضف إلى السلة",
    "outOfStock": "نفذت الكمية"
  }
}
</i18n>

<style lang="postcss" scoped>
.productCard_AddToCart {
  letter-spacing: 0.01em;
  transition: all 0.5s;

  &:hover {
    background: white;
  }
}
</style>
